import type { Landmark } from '@erectus/shared';

/**
 * Geometry helpers for posture metrics.
 *
 * Coordinate system: MediaPipe returns landmarks normalized to [0,1] against the
 * image *width* (x) and *height* (y) independently. On a 4:3 stream that makes a
 * horizontal unit 1.33× longer than a vertical one, which would skew every angle
 * by several degrees. Everything below therefore works in "plane space": x is
 * rescaled by the aspect ratio so both axes carry the same physical unit.
 *
 * y grows downward (image convention), so "up" is negative y.
 */
export interface Point {
  x: number;
  y: number;
}

/** Convert a normalized landmark into aspect-corrected plane space. */
export function toPlane(lm: Landmark, aspect: number): Point {
  return { x: lm.x * aspect, y: lm.y };
}

export function sub(a: Point, b: Point): Point {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function length(p: Point): number {
  return Math.hypot(p.x, p.y);
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export const RAD_TO_DEG = 180 / Math.PI;

/**
 * Angle of a vector against the horizontal, always in [0, 90].
 * 90° = straight up or down, 0° = perfectly horizontal.
 * This is the craniovertebral-angle convention: the angle between a horizontal
 * reference line and the line joining two landmarks.
 */
export function angleFromHorizontal(v: Point): number {
  const run = Math.abs(v.x);
  const rise = Math.abs(v.y);
  if (run === 0 && rise === 0) return 0;
  return Math.atan2(rise, run) * RAD_TO_DEG;
}

/**
 * Signed angle against the horizontal in [-90, 90]; positive = the vector points
 * upward (screen-up), negative = downward. `forwardSign` orients the horizontal
 * axis so the result does not flip when the subject faces the other way.
 */
export function signedAngleFromHorizontal(v: Point, forwardSign: number): number {
  const run = Math.abs(v.x * forwardSign);
  const rise = -v.y;
  if (run === 0 && rise === 0) return 0;
  return Math.atan2(rise, run) * RAD_TO_DEG;
}

/**
 * Signed angle against the vertical in [-180, 180]; positive = the vector tilts
 * toward the anterior (facing) direction.
 */
export function signedAngleFromVertical(v: Point, anteriorSign: number): number {
  const anterior = v.x * anteriorSign;
  const up = -v.y;
  if (anterior === 0 && up === 0) return 0;
  return Math.atan2(anterior, up) * RAD_TO_DEG;
}

/** Interior angle ABC in degrees, in [0, 180]. */
export function threePointAngle(a: Point, b: Point, c: Point): number {
  const ba = sub(a, b);
  const bc = sub(c, b);
  const magnitude = length(ba) * length(bc);
  if (magnitude === 0) return 0;
  const cos = (ba.x * bc.x + ba.y * bc.y) / magnitude;
  return Math.acos(Math.min(1, Math.max(-1, cos))) * RAD_TO_DEG;
}

/** Angle between two vectors in [0, 180] — used for wrist deviation. */
export function vectorAngle(u: Point, v: Point): number {
  const magnitude = length(u) * length(v);
  if (magnitude === 0) return 0;
  const cos = (u.x * v.x + u.y * v.y) / magnitude;
  return Math.acos(Math.min(1, Math.max(-1, cos))) * RAD_TO_DEG;
}
